import { ChangeEvent, useMemo, useRef, useState } from 'react';
import { CheckSquare, ImagePlus, Link2, List, Sparkles, TextQuote } from 'lucide-react';
import { MarkdownDescription } from './MarkdownDescription';

export type DescriptionSuggestionMode = 'clarify' | 'acceptance_criteria' | 'risk_review';

type DescriptionSuggestionContext = {
  title?: string;
  type?: string;
};

interface RichDescriptionEditorProps {
  value: string;
  onChange: (nextValue: string) => void;
  placeholder?: string;
  rows?: number;
  className?: string;
  showPreview?: boolean;
  onSave?: () => void;
  saveLabel?: string;
  canEdit?: boolean;
  suggestionContext?: DescriptionSuggestionContext;
  llmSuggestionProvider?: (
    mode: DescriptionSuggestionMode,
    currentValue: string,
    context?: DescriptionSuggestionContext
  ) => Promise<string>;
}

const IMAGE_PATTERN = /!\[[^\]]*\]\(([^)]+)\)/g;

const SUGGESTION_LABELS: Record<DescriptionSuggestionMode, string> = {
  clarify: 'Improve clarity',
  acceptance_criteria: 'Add acceptance criteria',
  risk_review: 'Add risks and edge-cases',
};

const LOCAL_SUGGESTION_TEMPLATES: Record<DescriptionSuggestionMode, (context?: DescriptionSuggestionContext) => string> = {
  clarify: (context) => {
    const title = context?.title ? `for ${context.title}` : 'for this task';
    return [
      '## Objective',
      `Deliver the requested outcome ${title} with clear and measurable scope.`,
      '',
      '## Scope',
      '- In scope:',
      '- Out of scope:',
      '',
      '## Expected Outcome',
      '- User/business impact:',
      '- Technical impact:',
    ].join('\n');
  },
  acceptance_criteria: (context) => {
    const typeHint = context?.type ? ` (${context.type})` : '';
    return [
      `## Acceptance Criteria${typeHint}`,
      '- [ ] Functional behavior is implemented as requested',
      '- [ ] Edge cases are handled gracefully',
      '- [ ] Errors are surfaced with clear messaging',
      '- [ ] Unit/manual test coverage is updated',
      '- [ ] No regressions in related workflows',
    ].join('\n');
  },
  risk_review: () => {
    return [
      '## Risks and Edge Cases',
      '- Data validation and malformed input handling',
      '- Permission/access control behavior',
      '- Loading, empty, and error states',
      '- Mobile responsiveness and overflow handling',
      '- Theme compatibility (light/dark/system)',
    ].join('\n');
  },
};

function appendText(current: string, snippet: string): string {
  return current ? `${current}\n${snippet}` : snippet;
}

export function RichDescriptionEditor({
  value,
  onChange,
  placeholder = 'Describe the task...',
  rows = 4,
  className = '',
  showPreview = true,
  onSave,
  saveLabel = 'Save Description',
  canEdit = true,
  suggestionContext,
  llmSuggestionProvider,
}: RichDescriptionEditorProps) {
  const imageInputRef = useRef<HTMLInputElement>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationError, setGenerationError] = useState<string>('');
  const [selectedSuggestionMode, setSelectedSuggestionMode] = useState<DescriptionSuggestionMode>('clarify');

  const descriptionImageUrls = useMemo(() => {
    const matches = Array.from(value.matchAll(IMAGE_PATTERN));
    return matches.map((match) => match[1]).filter(Boolean);
  }, [value]);

  if (!canEdit) {
    if (!value.trim()) {
      return <p className="text-muted-foreground italic">No description provided</p>;
    }

    return <MarkdownDescription content={value} className="text-muted-foreground" />;
  }

  const appendSnippet = (snippet: string) => {
    onChange(appendText(value, snippet));
  };

  const handleInsertHeading = () => appendSnippet('## Section Title');
  const handleInsertBulletList = () => appendSnippet('- Item 1\n- Item 2\n- Item 3');
  const handleInsertChecklist = () => appendSnippet('- [ ] Checklist item 1\n- [ ] Checklist item 2');
  const handleInsertBoldTemplate = () => appendSnippet('**Important:** ');

  const handleInsertLink = () => {
    const rawUrl = window.prompt('Enter link URL');
    const url = String(rawUrl || '').trim();
    if (!url) return;

    const rawLabel = window.prompt('Enter link text (optional)');
    const label = String(rawLabel || '').trim() || 'Link';
    appendSnippet(`[${label}](${url})`);
  };

  const handleImageUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);

    files.forEach((file, index) => {
      if (!file.type.startsWith('image/')) return;

      const reader = new FileReader();
      reader.onload = (loadEvent) => {
        const imageUrl = String(loadEvent.target?.result || '').trim();
        if (!imageUrl) return;

        const safeName = file.name?.trim() || `Image ${index + 1}`;
        appendSnippet(`![${safeName}](${imageUrl})`);
      };
      reader.readAsDataURL(file);
    });

    if (event.target) {
      event.target.value = '';
    }
  };

  const handleGenerateSuggestion = async () => {
    setGenerationError('');
    setIsGenerating(true);

    try {
      const suggestion = llmSuggestionProvider
        ? await llmSuggestionProvider(selectedSuggestionMode, value, suggestionContext)
        : LOCAL_SUGGESTION_TEMPLATES[selectedSuggestionMode](suggestionContext);

      const normalized = String(suggestion || '').trim();
      if (!normalized) {
        return;
      }

      appendSnippet(normalized);
    } catch (error) {
      setGenerationError(
        error instanceof Error ? error.message : 'Could not generate a suggestion right now.'
      );
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className={`space-y-3 ${className}`.trim()}>
      <textarea
        value={value}
        onChange={(event) => onChange(event.target.value)}
        rows={rows}
        className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring bg-muted/40"
        placeholder={placeholder}
      />

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={handleInsertHeading}
          className="inline-flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted/40 dark:hover:bg-muted/40"
        >
          <TextQuote className="h-4 w-4" />
          Heading
        </button>
        <button
          type="button"
          onClick={handleInsertBulletList}
          className="inline-flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted/40 dark:hover:bg-muted/40"
        >
          <List className="h-4 w-4" />
          Bullets
        </button>
        <button
          type="button"
          onClick={handleInsertChecklist}
          className="inline-flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted/40 dark:hover:bg-muted/40"
        >
          <CheckSquare className="h-4 w-4" />
          Checklist
        </button>
        <button
          type="button"
          onClick={handleInsertBoldTemplate}
          className="inline-flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-sm font-semibold text-muted-foreground hover:bg-muted/40 dark:hover:bg-muted/40"
        >
          B
          Bold
        </button>
        <button
          type="button"
          onClick={handleInsertLink}
          className="inline-flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted/40 dark:hover:bg-muted/40"
        >
          <Link2 className="h-4 w-4" />
          Add Link
        </button>
        <button
          type="button"
          onClick={() => imageInputRef.current?.click()}
          className="inline-flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted/40 dark:hover:bg-muted/40"
        >
          <ImagePlus className="h-4 w-4" />
          Add Picture
        </button>
        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={handleImageUpload}
          aria-label="Upload description images"
        />
      </div>

      <div className="rounded-lg border border-primary/40 bg-primary/10 p-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-primary">
            <Sparkles className="h-3.5 w-3.5" />
            AI Suggestions
          </span>
          <select
            title="Suggestion mode"
            value={selectedSuggestionMode}
            onChange={(event) => setSelectedSuggestionMode(event.target.value as DescriptionSuggestionMode)}
            className="rounded-md border border-primary/40 bg-white px-2 py-1 text-xs text-primary bg-muted/40"
          >
            {(Object.keys(SUGGESTION_LABELS) as DescriptionSuggestionMode[]).map((mode) => (
              <option key={mode} value={mode}>
                {SUGGESTION_LABELS[mode]}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={handleGenerateSuggestion}
            disabled={isGenerating}
            className="rounded-md bg-primary/10 px-2.5 py-1 text-xs font-medium text-white hover:bg-primary/10 disabled:cursor-not-allowed disabled:opacity-70"
          >
            {isGenerating ? 'Generating...' : 'Insert Suggestion'}
          </button>
          <span className="text-[11px] text-primary">
            Ready for external LLM provider integration.
          </span>
        </div>
        {generationError && <p className="mt-2 text-xs text-destructive">{generationError}</p>}
      </div>

      {descriptionImageUrls.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {descriptionImageUrls.map((url, index) => (
            <a
              key={`${url}-${index}`}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex"
            >
              <img
                src={url}
                alt={`Description image ${index + 1}`}
                className="h-16 w-16 rounded-lg border border-border object-cover"
              />
            </a>
          ))}
        </div>
      )}

      {showPreview && (
        <div className="rounded-lg border border-border bg-muted/40 p-3">
          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">Preview</p>
          {value.trim() ? (
            <MarkdownDescription content={value} className="text-muted-foreground" />
          ) : (
            <p className="text-sm italic text-muted-foreground">No description provided</p>
          )}
        </div>
      )}

      {onSave && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onSave}
            className="inline-flex items-center gap-1 rounded-lg bg-primary/10 px-3 py-1.5 text-sm text-white hover:bg-primary/10"
          >
            {saveLabel}
          </button>
        </div>
      )}
    </div>
  );
}
